// Pure decision logic for CallToSubscribeBanner, extracted so it can be unit
// tested in the existing ts-jest node environment (the repo has no jsdom/RTL —
// same convention as pages/billing/billingPageLogic.ts).

export type SubscribeBannerKind =
    | 'none'
    | 'expired-owner'
    | 'expired-not-owner'
    | 'trial-owner'
    | 'trial-not-owner';

export interface SubscribeBannerInput {
    isAdmin: boolean;
    isOwner: boolean;
    orgHasHypercareEnabled: boolean;
    nxVetSubscriptionStatus: string | undefined;
    nxHubSubscriptionStatus: string | undefined;
    // NxVET Clip: Unlimited sub status, and whether the org owns a Clip at all
    // (Free plan). Both suppress the free-trial nudge — a Free Clip customer
    // paid for hardware and is on the plan they chose.
    nxClipSubscriptionStatus?: string | undefined;
    isNxClipCustomer?: boolean;
    // Whether nxVetForceSubscriptionEndDate is set. When it is, the banner is
    // deliberately shown even to admins / orgs whose subscription status reads
    // Active or HandledByOther — a forced (temporary) NxVET window is ending
    // and the org must be warned, paid NxHUB sub or not.
    hasNxVetForceSubscriptionEndDate: boolean;
}

const isActiveish = (status: string | undefined): boolean =>
    status === 'Active' || status === 'HandledByOther';

export function subscribeBannerKind(input: SubscribeBannerInput): SubscribeBannerKind {
    // Hypercare-type orgs don't run on the nx.vet subscription model, so never
    // nudge them to upgrade / subscribe.
    if (input.orgHasHypercareEnabled) {
        return 'none';
    }

    // Suppress for admins and genuinely subscribed orgs. A paid NxHUB
    // subscription (dedicated split-sub or legacy merged, ADR 0001) counts: an
    // NxHUB-only org has no NxVET sub, so the NxVET status alone reads
    // "NotSubscribed" and would show a paying customer the free-trial nudge.
    // The forced-end-date exception applies to ALL of these, NxHUB included —
    // see hasNxVetForceSubscriptionEndDate above.
    if (input.isAdmin
        || isActiveish(input.nxVetSubscriptionStatus)
        || isActiveish(input.nxHubSubscriptionStatus)
        || isActiveish(input.nxClipSubscriptionStatus)
        || !!input.isNxClipCustomer) {
        if (!input.hasNxVetForceSubscriptionEndDate) {
            return 'none';
        }
    }

    if (input.nxVetSubscriptionStatus === 'Expired') {
        return input.isOwner ? 'expired-owner' : 'expired-not-owner';
    }

    return input.isOwner ? 'trial-owner' : 'trial-not-owner';
}
